import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Sparkles, Loader2 } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_agency-audit-tool/artifacts/aj4ztq87_image.png";

export default function Login() {
  const { getGoogleAuthUrl, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [loggingIn, setLoggingIn] = useState(false);
  const [showCredentials, setShowCredentials] = useState(false);

  const handleGoogleLogin = () => {
    window.location.href = getGoogleAuthUrl();
  };

  const handleCredentialLogin = async (e) => {
    e.preventDefault();
    if (!username || !password) return;
    setLoggingIn(true);
    try {
      const resp = await axios.post(`${API}/auth/login`, { username, password });
      if (resp.data.session_token) {
        localStorage.setItem("impress360_session_token", resp.data.session_token);
      }
      await refreshUser();
      toast.success(`Welcome, ${resp.data.name}!`);
      navigate("/", { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Login failed");
    } finally {
      setLoggingIn(false);
    }
  };

  return (
    <div className="min-h-screen bg-zinc-950 bg-grid relative overflow-hidden flex items-center justify-center">
      <div className="absolute inset-0 bg-gradient-to-br from-zinc-950 via-zinc-900/50 to-zinc-950 pointer-events-none" />
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[600px] bg-primary/5 rounded-full blur-3xl pointer-events-none" />
      
      <div className="relative z-10 w-full max-w-md px-4">
        <Card className="card-glass glow-yellow-sm">
          <CardContent className="p-8">
            <div className="text-center mb-8">
              <img src={LOGO_URL} alt="Digital Impressions" className="h-14 w-auto mx-auto mb-6" />
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 border border-primary/20 mb-4">
                <Sparkles className="w-4 h-4 text-primary" />
                <span className="text-sm text-primary font-medium">IMPRESS 360 Audit</span>
              </div>
              <h1 className="heading-lg text-white mb-2">Welcome Back</h1>
              <p className="text-zinc-400">Sign in to access your audits and reports</p>
            </div>

            {/* Google Login - Primary */}
            <Button
              onClick={handleGoogleLogin}
              className="w-full h-12 bg-white hover:bg-zinc-100 text-zinc-900 font-medium rounded-xl flex items-center justify-center gap-3"
              data-testid="google-login-btn"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
              </svg>
              Continue with Google
            </Button>

            {/* Collapsible credentials login */}
            <div className="mt-6">
              {!showCredentials ? (
                <button
                  onClick={() => setShowCredentials(true)}
                  className="w-full text-center text-xs text-zinc-500 hover:text-zinc-400 transition-colors"
                  data-testid="show-credentials-btn"
                >
                  Sign in with organization credentials
                </button>
              ) : (
                <div className="animate-in fade-in slide-in-from-top-2 duration-200">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="flex-1 h-px bg-zinc-800" />
                    <span className="text-xs text-zinc-500">Organization Login</span>
                    <div className="flex-1 h-px bg-zinc-800" />
                  </div>
                  <form onSubmit={handleCredentialLogin} className="space-y-3">
                    <Input
                      type="text"
                      placeholder="Username"
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      className="h-10 bg-zinc-900/60 border-zinc-800 text-white placeholder:text-zinc-500 rounded-xl text-sm"
                      data-testid="username-input"
                    />
                    <Input
                      type="password"
                      placeholder="Password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="h-10 bg-zinc-900/60 border-zinc-800 text-white placeholder:text-zinc-500 rounded-xl text-sm"
                      data-testid="password-input"
                    />
                    <Button
                      type="submit"
                      disabled={loggingIn || !username || !password}
                      variant="outline"
                      className="w-full h-10 border-zinc-700 text-zinc-300 hover:bg-zinc-800 rounded-xl text-sm"
                      data-testid="login-submit-btn"
                    >
                      {loggingIn ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                      Sign In
                    </Button>
                  </form>
                </div>
              )}
            </div>

            <p className="text-center text-xs text-zinc-500 mt-6">
              By signing in, you agree to our Terms of Service and Privacy Policy
            </p>
          </CardContent>
        </Card>

        <p className="text-center text-zinc-600 text-sm mt-6">
          Powered by Digital Impressions
        </p>
      </div>
    </div>
  );
}
